import { useCallback, useMemo, useState } from 'react';
import {
  DEFAULT_PAGE_SIZE,
  type ReportStatus,
  WORKLIST_MIN_SEARCH,
  type WorklistPage,
  type WorklistSort,
} from '#shared/types';
import { useDebounced } from '#client/hooks/useDebounced';
import { useKeyedRequest } from '#client/hooks/useKeyedRequest';
import { useUrlState } from '#client/hooks/useUrlState';
import { api } from '#client/lib/api';
import { RESOURCE } from '#client/lib/routes';
import { parseEnum } from '#client/lib/url-params';

/**
 * The worklist's data and its filter state, both backed by the URL.
 *
 * Every filter, the sort, the tab and the page live in the query string, so a
 * view is shareable, survives a refresh, and the back button steps through it.
 *
 * The two general parts live elsewhere: `useUrlState` owns reading and writing
 * the query string, `useKeyedRequest` owns the abortable fetch. What is left
 * here is what is genuinely about a worklist — which params exist, how they are
 * parsed, and what they mean.
 */

const DEFAULT_SORT: WorklistSort = 'due_asc';
const FILTER_FLAGS = ['overdue', 'unpaid', 'mine'] as const;

export type WorklistFlag = (typeof FILTER_FLAGS)[number];

export interface WorklistFilters {
  /**
   * Always set. There is no "all statuses" view: the screen is built around
   * three tabs, and a bare `/worklist` with no status showed every report while
   * the tab bar highlighted "Registered" — the tab claiming one thing and the
   * table showing another.
   */
  status: ReportStatus;
  search: string;
  sort: WorklistSort;
  page: number;
  overdue: boolean;
  unpaid: boolean;
  mine: boolean;
}

interface UseWorklistResult {
  filters: WorklistFilters;
  data?: WorklistPage | undefined;
  loading: boolean;
  error?: string | undefined;
  limit: number;
  setStatus: (status: ReportStatus) => void;
  setSearch: (value: string) => void;
  setSort: (sort: WorklistSort) => void;
  setPage: (page: number) => void;
  toggle: (flag: WorklistFlag) => void;
  clearFilters: () => void;
  /** True when anything beyond the status tab is narrowing the list. */
  hasFilters: boolean;
}

const STATUSES: readonly ReportStatus[] = ['registered', 'in_progress', 'completed'];
const SORTS: readonly WorklistSort[] = [
  'due_asc',
  'due_desc',
  'created_desc',
  'created_asc',
  'patient_asc',
];

function parseFilters(params: URLSearchParams): WorklistFilters {
  const status = params.get('status');
  const sort = params.get('sort');
  const page = Number(params.get('page') ?? '1');

  return {
    // Matched against the known values rather than cast, so a hand-edited URL
    // cannot put an unknown status on the wire. Falls back to the first tab.
    status: parseEnum(status, STATUSES, 'registered'),
    search: params.get('search') ?? '',
    sort: parseEnum(sort, SORTS, DEFAULT_SORT),
    page: Number.isFinite(page) && page >= 1 ? Math.floor(page) : 1,
    overdue: params.get('overdue') === 'true',
    unpaid: params.get('unpaid') === 'true',
    mine: params.get('mine') === 'true',
  };
}

export function useWorklist(): UseWorklistResult {
  const { params, setParam, toggleParam, clearParams } = useUrlState();
  const filters = useMemo(() => parseFilters(params), [params]);

  // Typing should not push a history entry per keystroke, so the search box is
  // debounced before it reaches the URL and the request. The draft is what the
  // input renders, so typing stays responsive.
  const [searchDraft, setSearchDraft] = useState(filters.search);
  const debouncedSearch = useDebounced(searchDraft, 300);

  const query = useMemo(
    () => ({
      status: filters.status,
      // The API refuses a one-character term (see WORKLIST_MIN_SEARCH), so
      // typing waits for the second rather than flashing an error.
      search:
        debouncedSearch.trim().length >= WORKLIST_MIN_SEARCH ? debouncedSearch.trim() : undefined,
      sort: filters.sort,
      page: filters.page,
      limit: DEFAULT_PAGE_SIZE,
      overdue: filters.overdue ? 'true' : undefined,
      unpaid: filters.unpaid ? 'true' : undefined,
      mine: filters.mine ? 'true' : undefined,
    }),
    [filters, debouncedSearch],
  );

  const request = useKeyedRequest<WorklistPage>(
    JSON.stringify(query),
    (signal) => api.get<WorklistPage>(RESOURCE.reports, query, signal),
    'Could not load the worklist',
  );

  const setStatus = useCallback((status: ReportStatus) => setParam('status', status), [setParam]);

  const setSearch = useCallback(
    (value: string) => {
      setSearchDraft(value);
      setParam('search', value, { replace: true });
    },
    [setParam],
  );

  const setSort = useCallback(
    (sort: WorklistSort) => setParam('sort', sort === DEFAULT_SORT ? undefined : sort),
    [setParam],
  );

  const setPage = useCallback(
    (page: number) => setParam('page', page <= 1 ? undefined : String(page), { keepPage: true }),
    [setParam],
  );

  const toggle = useCallback((flag: WorklistFlag) => toggleParam(flag), [toggleParam]);

  const clearFilters = useCallback(() => {
    setSearchDraft('');
    clearParams(['search', ...FILTER_FLAGS]);
  }, [clearParams]);

  return useMemo(
    () => ({
      filters: { ...filters, search: searchDraft },
      data: request.data,
      loading: request.loading,
      error: request.error,
      limit: DEFAULT_PAGE_SIZE,
      setStatus,
      setSearch,
      setSort,
      setPage,
      toggle,
      clearFilters,
      hasFilters: Boolean(searchDraft) || filters.overdue || filters.unpaid || filters.mine,
    }),
    [filters, searchDraft, request, setStatus, setSearch, setSort, setPage, toggle, clearFilters],
  );
}
